import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Filter, X, ChevronDown, ChevronRight, Search, UtensilsCrossed, Palette, Sparkles } from 'lucide-react';
import { api } from '../context/AuthContext';
import ProductCard from '../components/ProductCard';
import { useSEO } from '../hooks/useSEO';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';

// Configurazione macro types
const MACRO_TYPE_CONFIG = {
  FOOD: { label: 'Food', icon: UtensilsCrossed, color: 'text-orange-600', bgColor: 'bg-orange-50', borderColor: 'border-orange-200' },
  ARTIGIANATO: { label: 'Artigianato', icon: Palette, color: 'text-blue-600', bgColor: 'bg-blue-50', borderColor: 'border-blue-200' },
  EXPERIENCE: { label: 'Experience', icon: Sparkles, color: 'text-purple-600', bgColor: 'bg-purple-50', borderColor: 'border-purple-200' }
};

// Componente per sezione categorie collapsible
const CategorySection = ({ macroType, categories, selectedCategory, onCategorySelect, isCollapsed, onToggleCollapse }) => {
  const config = MACRO_TYPE_CONFIG[macroType];
  if (!config || categories.length === 0) return null;
  
  const Icon = config.icon;
  
  return (
    <div className={`rounded-lg border ${config.borderColor} ${config.bgColor} mb-3`}>
      <button
        onClick={onToggleCollapse}
        className="w-full flex items-center justify-between p-3 hover:bg-white/50 transition-colors rounded-t-lg"
        data-testid={`category-section-${macroType.toLowerCase()}`}
      >
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${config.color}`} />
          <span className={`font-medium ${config.color}`}>{config.label}</span>
          <span className="text-xs text-text-muted">({categories.length})</span>
        </div>
        {isCollapsed ? (
          <ChevronRight className="w-4 h-4 text-text-muted" />
        ) : (
          <ChevronDown className="w-4 h-4 text-text-muted" />
        )}
      </button>
      
      {!isCollapsed && (
        <div className="px-3 pb-3 space-y-1">
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => onCategorySelect(cat.slug === selectedCategory ? '' : cat.slug)}
              className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                cat.slug === selectedCategory
                  ? 'bg-primary text-white'
                  : 'hover:bg-white/80 text-text-secondary'
              }`}
              data-testid={`category-item-${cat.slug}`}
            >
              {cat.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ProductsPage = () => {
  useSEO({
    title: 'Prodotti',
    description: 'Sfoglia il catalogo di prodotti artigianali e alimentari del Sud Italia: cibo, artigianato ed esperienze. Acquista direttamente dai produttori locali.',
    canonicalPath: '/prodotti',
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [regions, setRegions] = useState([]);
  const [macroCmsConfig, setMacroCmsConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [filtersOpen, setFiltersOpen] = useState(false);
  
  // Stato per sezioni collapsible (default: tutte aperte)
  const [collapsedSections, setCollapsedSections] = useState({
    FOOD: false,
    ARTIGIANATO: false,
    EXPERIENCE: false
  });
  
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    region: searchParams.get('region') || '',
    search: searchParams.get('search') || '',
    sort: searchParams.get('sort') || 'newest',
    page: parseInt(searchParams.get('page') || '1'),
    macro_type: searchParams.get('macro_type') || '' // Nuovo filtro
  });

  // Raggruppa categorie per macro_type
  const categoriesByMacroType = useMemo(() => {
    const grouped = { FOOD: [], ARTIGIANATO: [], EXPERIENCE: [] };
    categories.forEach(cat => {
      const macroType = cat.macro_type || 'FOOD';
      if (grouped[macroType]) {
        grouped[macroType].push(cat);
      }
    });
    return grouped;
  }, [categories]);

  // Categorie filtrate in base al macro_type selezionato
  const filteredCategories = useMemo(() => {
    if (filters.macro_type) {
      return categories.filter(cat => cat.macro_type === filters.macro_type);
    }
    return categories;
  }, [categories, filters.macro_type]);

  // Toggle sezione collapsible
  const toggleSection = (macroType) => {
    setCollapsedSections(prev => ({
      ...prev,
      [macroType]: !prev[macroType]
    }));
  };

  // Titolo pagina dinamico — preferisce CMS, fallback al config locale
  const pageTitle = useMemo(() => {
    if (filters.macro_type) {
      return macroCmsConfig[filters.macro_type]?.title || MACRO_TYPE_CONFIG[filters.macro_type]?.label || filters.macro_type;
    }
    return 'I Nostri Prodotti';
  }, [filters.macro_type, macroCmsConfig]);

  // Descrizione pagina dinamica — preferisce CMS, fallback hardcoded
  const pageDescription = useMemo(() => {
    if (filters.macro_type && macroCmsConfig[filters.macro_type]?.description) {
      return macroCmsConfig[filters.macro_type].description;
    }
    switch (filters.macro_type) {
      case 'FOOD': return 'Scopri le eccellenze gastronomiche del Sud Italia';
      case 'ARTIGIANATO': return 'Artigianato autentico dalle tradizioni del Sud';
      case 'EXPERIENCE': return 'Esperienze uniche nel cuore del Mezzogiorno';
      default: return 'Scopri le eccellenze del Sud Italia';
    }
  }, [filters.macro_type, macroCmsConfig]);

  useEffect(() => {
    const fetchCatalog = async () => {
      try {
        const [catRes, regRes, macroRes] = await Promise.all([
          api.get('/catalog/categories'),
          api.get('/catalog/regions'),
          api.get('/cms/macro-types'),
        ]);
        setCategories(catRes.data);
        setRegions(regRes.data);
        // Indicizza per macro_type per accesso rapido
        const macroMap = {};
        for (const m of macroRes.data) macroMap[m.macro_type] = m;
        setMacroCmsConfig(macroMap);
      } catch (error) {
        console.error('Error fetching catalog:', error);
      }
    };
    fetchCatalog();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filters.category) params.set('category', filters.category);
        if (filters.region) params.set('region', filters.region);
        if (filters.search) params.set('search', filters.search);
        if (filters.macro_type) params.set('macro_type', filters.macro_type);
        params.set('sort', filters.sort);
        params.set('page', filters.page.toString());
        params.set('page_size', '12');

        const res = await api.get(`/products?${params.toString()}`);
        
        setProducts(res.data.items);
        setPagination({
          total: res.data.total,
          page: res.data.page,
          pages: res.data.pages
        });
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();

    const params = new URLSearchParams();
    if (filters.category) params.set('category', filters.category);
    if (filters.region) params.set('region', filters.region);
    if (filters.search) params.set('search', filters.search);
    if (filters.macro_type) params.set('macro_type', filters.macro_type);
    if (filters.sort !== 'newest') params.set('sort', filters.sort);
    if (filters.page > 1) params.set('page', filters.page.toString());
    setSearchParams(params);
  }, [filters, setSearchParams]);

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({ category: '', region: '', search: '', sort: 'newest', page: 1, macro_type: '' });
  };

  // Rimuove solo il filtro macro_type
  const clearMacroTypeFilter = () => {
    setFilters(prev => ({ ...prev, macro_type: '', page: 1 }));
  };

  const hasActiveFilters = filters.category || filters.region || filters.search || filters.macro_type;

  return (
    <div className="min-h-screen bg-background" data-testid="products-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Banner macro-tipo (solo quando filtro attivo e immagine CMS disponibile) */}
        {filters.macro_type && macroCmsConfig[filters.macro_type]?.image_url && (
          <div className="relative h-48 md:h-64 rounded-xl overflow-hidden mb-8 -mx-4 sm:mx-0">
            <img
              src={macroCmsConfig[filters.macro_type].image_url}
              alt={pageTitle}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
            <div className="absolute bottom-0 left-0 p-6">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-white">
                {pageTitle}
              </h1>
              <p className="mt-1 text-white/90 text-sm md:text-base">{pageDescription}</p>
            </div>
          </div>
        )}

        {/* Breadcrumb + Header */}
        <div className="mb-8">
          {filters.macro_type && (
            <nav className="flex items-center gap-2 text-sm mb-4">
              <Link to="/prodotti" className="text-text-muted hover:text-primary">
                Tutti i Prodotti
              </Link>
              <span className="text-text-muted">/</span>
              <span className={MACRO_TYPE_CONFIG[filters.macro_type]?.color || 'text-text-primary'}>
                {MACRO_TYPE_CONFIG[filters.macro_type]?.label || filters.macro_type}
              </span>
            </nav>
          )}
          {/* Titolo testuale solo se non c'è già il banner con titolo embedded */}
          {!(filters.macro_type && macroCmsConfig[filters.macro_type]?.image_url) && (
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-text-primary">
              {pageTitle}
            </h1>
          )}
          <p className="mt-2 text-text-secondary">
            {!(filters.macro_type && macroCmsConfig[filters.macro_type]?.image_url) && pageDescription}
          </p>
          
          {/* Macro Type Pills - mostrati solo se non c'è un macro_type attivo */}
          {!filters.macro_type && (
            <div className="flex flex-wrap gap-2 mt-4">
              {Object.entries(MACRO_TYPE_CONFIG).map(([key, config]) => {
                const Icon = config.icon;
                return (
                  <button
                    key={key}
                    onClick={() => updateFilter('macro_type', key)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full border ${config.borderColor} ${config.bgColor} hover:shadow-sm transition-all`}
                    data-testid={`macro-pill-${key.toLowerCase()}`}
                  >
                    <Icon className={`w-4 h-4 ${config.color}`} />
                    <span className={`text-sm font-medium ${config.color}`}>{config.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Filters Bar */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
            <Input
              type="text"
              placeholder="Cerca prodotti..."
              value={filters.search}
              onChange={(e) => updateFilter('search', e.target.value)}
              className="pl-10"
              data-testid="search-products-input"
            />
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setFiltersOpen(!filtersOpen)}
              className="md:hidden"
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filtri
              {hasActiveFilters && (
                <span className="ml-2 w-2 h-2 bg-primary rounded-full" />
              )}
            </Button>
            
            <div className="hidden md:flex gap-2">
              <Select value={filters.category || "all"} onValueChange={(v) => updateFilter('category', v === "all" ? "" : v)}>
                <SelectTrigger className="w-[180px]" data-testid="category-filter">
                  <SelectValue placeholder="Categoria" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="all">Tutte le categorie</SelectItem>
                  {/* Se c'è un macro_type attivo, mostra solo quelle categorie */}
                  {filters.macro_type ? (
                    filteredCategories.map(cat => (
                      <SelectItem key={cat.id} value={cat.slug}>{cat.name}</SelectItem>
                    ))
                  ) : (
                    /* Altrimenti raggruppa per macro_type */
                    Object.entries(MACRO_TYPE_CONFIG).map(([macroKey, config]) => {
                      const macroCategories = categoriesByMacroType[macroKey] || [];
                      if (macroCategories.length === 0) return null;
                      const Icon = config.icon;
                      return (
                        <React.Fragment key={macroKey}>
                          <div className={`px-2 py-1.5 text-xs font-semibold ${config.color} flex items-center gap-1.5 ${config.bgColor} -mx-1 mt-1`}>
                            <Icon className="w-3 h-3" />
                            {config.label}
                          </div>
                          {macroCategories.map(cat => (
                            <SelectItem key={cat.id} value={cat.slug} className="pl-6">{cat.name}</SelectItem>
                          ))}
                        </React.Fragment>
                      );
                    })
                  )}
                </SelectContent>
              </Select>

              <Select value={filters.region || "all"} onValueChange={(v) => updateFilter('region', v === "all" ? "" : v)}>
                <SelectTrigger className="w-[160px]" data-testid="region-filter">
                  <SelectValue placeholder="Regione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte</SelectItem>
                  {regions.map(reg => (
                    <SelectItem key={reg.id} value={reg.id}>{reg.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Select value={filters.sort} onValueChange={(v) => updateFilter('sort', v)}>
              <SelectTrigger className="w-[140px]" data-testid="sort-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Più recenti</SelectItem>
                <SelectItem value="price_asc">Prezzo: basso</SelectItem>
                <SelectItem value="price_desc">Prezzo: alto</SelectItem>
                <SelectItem value="popular">Più popolari</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Mobile Filters Panel */}
        {filtersOpen && (
          <div className="md:hidden mb-6 p-4 bg-white rounded-xl border border-border">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Filtri</h3>
              <button onClick={() => setFiltersOpen(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              {/* Categorie raggruppate per macro_type */}
              <div>
                <label className="text-sm text-text-secondary mb-2 block">Categoria</label>
                {filters.macro_type ? (
                  /* Se c'è macro_type attivo, mostra solo quelle categorie in lista */
                  <div className="space-y-1">
                    <button
                      onClick={() => updateFilter('category', '')}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm ${
                        !filters.category ? 'bg-primary text-white' : 'bg-muted/30 text-text-secondary'
                      }`}
                    >
                      Tutte le categorie
                    </button>
                    {filteredCategories.map(cat => (
                      <button
                        key={cat.id}
                        onClick={() => updateFilter('category', cat.slug === filters.category ? '' : cat.slug)}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm ${
                          cat.slug === filters.category ? 'bg-primary text-white' : 'hover:bg-muted/30'
                        }`}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                ) : (
                  /* Senza macro_type, mostra struttura gerarchica collapsible */
                  <div className="space-y-2">
                    {Object.entries(MACRO_TYPE_CONFIG).map(([macroKey, config]) => (
                      <CategorySection
                        key={macroKey}
                        macroType={macroKey}
                        categories={categoriesByMacroType[macroKey] || []}
                        selectedCategory={filters.category}
                        onCategorySelect={(slug) => updateFilter('category', slug)}
                        isCollapsed={collapsedSections[macroKey]}
                        onToggleCollapse={() => toggleSection(macroKey)}
                      />
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="text-sm text-text-secondary mb-1 block">Regione</label>
                <Select value={filters.region || "all"} onValueChange={(v) => updateFilter('region', v === "all" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tutte le regioni" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte</SelectItem>
                    {regions.map(reg => (
                      <SelectItem key={reg.id} value={reg.id}>{reg.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {/* Active Filters */}
        {hasActiveFilters && (
          <div className="flex items-center gap-2 mb-6 flex-wrap">
            <span className="text-sm text-text-secondary">Filtri attivi:</span>
            {filters.macro_type && MACRO_TYPE_CONFIG[filters.macro_type] && (
              <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${MACRO_TYPE_CONFIG[filters.macro_type].bgColor} ${MACRO_TYPE_CONFIG[filters.macro_type].color}`}>
                {(() => {
                  const Icon = MACRO_TYPE_CONFIG[filters.macro_type].icon;
                  return <Icon className="w-3 h-3" />;
                })()}
                {MACRO_TYPE_CONFIG[filters.macro_type].label}
                <button onClick={clearMacroTypeFilter}>
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {filters.category && (
              <span className="inline-flex items-center gap-1 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm">
                {categories.find(c => c.slug === filters.category)?.name}
                <button onClick={() => updateFilter('category', '')}>
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {filters.region && (
              <span className="inline-flex items-center gap-1 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm">
                {regions.find(r => r.id === filters.region)?.name}
                <button onClick={() => updateFilter('region', '')}>
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {filters.search && (
              <span className="inline-flex items-center gap-1 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm">
                "{filters.search}"
                <button onClick={() => updateFilter('search', '')}>
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            <button onClick={clearFilters} className="text-sm text-text-secondary hover:text-primary">
              Cancella tutti
            </button>
          </div>
        )}

        {/* Results */}
        <div className="mb-4 text-sm text-text-secondary">
          {pagination.total} prodotti trovati
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl overflow-hidden animate-pulse">
                <div className="aspect-square bg-gray-200" />
                <div className="p-4">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-4 bg-gray-200 rounded w-1/2 mt-2" />
                </div>
              </div>
            ))}
          </div>
        ) : products.length > 0 ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {products.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="mt-8 flex justify-center gap-2">
                <Button
                  variant="outline"
                  disabled={pagination.page === 1}
                  onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                >
                  Precedente
                </Button>
                <span className="flex items-center px-4 text-sm text-text-secondary">
                  Pagina {pagination.page} di {pagination.pages}
                </span>
                <Button
                  variant="outline"
                  disabled={pagination.page === pagination.pages}
                  onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                >
                  Successiva
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16 bg-white rounded-xl">
            <p className="text-text-secondary">Nessun prodotto trovato</p>
            {hasActiveFilters && (
              <Button variant="link" onClick={clearFilters} className="mt-2">
                Cancella i filtri
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsPage;
